/*
 * Created on 07.11.2018
 *
 * Copyright(c) 1995 - 2018 T-Systems Multimedia Solutions GmbH
 * Riesaer Str. 5, 01129 Dresden
 * All rights reserved.
 */
import { Component, ContentChild, EventEmitter, Input, OnInit, Output, TemplateRef } from '@angular/core';
import { slideContent } from '../../common-ui-common-animations';
import { LoggingService } from '../../utils/logging.service';
import { AsideItemService } from './aside-item-service';

// Default Icon-Class falls keine angegeben wird.
const DEFAULT_ICON_CSS_CLASS = 'defaulticon';

// default
export const SPP_OVERLAY_POSITION_SIDEBAR_LEFT_CENTER = [
    { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top' }
];

export const SPP_OVERLAY_POSITION_SIDEBAR_TOP_CENTER = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' }
];

export const SPP_OVERLAY_POSITION_SIDEBAR_RIGHT_CENTER = [
    { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top' }
];

export const SPP_OVERLAY_POSITION_SIDEBAR_BOTTOM_CENTER = [
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' }
];

// Position Konfiguration für die untere rechte Ecke. Das Overlay geht nach oben links auf
export const SPP_OVERLAY_POSITION_SIDEBAR_BOTTOM_RIGHT = [
    { originX: 'start', originY: 'bottom', overlayX: 'end', overlayY: 'bottom' }
];

/**
 * Aside Komponente bietet Overlay Technologie um Content darin zu öffnen. Die Komponente wird durch Asides gesteuert, die
 * am Rand des Browsers meist static angepinnt sind.
 * @author thm
 */

@Component({
    selector: 'common-aside-item-with-overlay',
    template: `
        <div class="sidebarItem" [ngClass]="{ 'activeOverlay': isOpen }" cdkOverlayOrigin
             #origin="cdkOverlayOrigin" (click)="toggleSidebarItem()">
            <span *ngIf="!isOpen" [ibtn]="icon" ibtnUnboxed class="toggleIcon"></span>
            <span *ngIf="isOpen" ibtn="back" ibtnUnboxed class="toggleIcon"></span>
        </div>
        <ng-template
            cdk-connected-overlay
            cdkConnectedOverlayHasBackdrop
            cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
            [cdkConnectedOverlayOrigin]="origin"
            [cdkConnectedOverlayPositions]="overlayPosition"
            [cdkConnectedOverlayOpen]="isOpen"
            (backdropClick)="toggleSidebarItem()"
            [cdkConnectedOverlayFlexibleDimensions]="false">
            <div *ngIf="inheritedOverlayPosition === 'left-center'" [@slideContent_lc]="stateExpression"
                 (@slideContent_lc.start)="animationStarted($event)">
                <ng-container [ngTemplateOutlet]="tpl"></ng-container>
            </div>
            <div *ngIf="inheritedOverlayPosition === 'right-center' || inheritedOverlayPosition === 'bottom-right'"
                 [@slideContent_rc]="stateExpression"
                 (@slideContent_rc.start)="animationStarted($event)">
                <ng-container [ngTemplateOutlet]="tpl"></ng-container>
            </div>
            <div *ngIf="inheritedOverlayPosition === 'top-center'" [@slideContent_tc]="stateExpression"
                 (@slideContent_tc.start)="animationStarted($event)">
                <ng-container [ngTemplateOutlet]="tpl"></ng-container>
            </div>
            <div *ngIf="inheritedOverlayPosition === 'bottom-center'" [@slideContent_bc]="stateExpression"
                 (@slideContent_bc.start)="animationStarted($event)">
                <ng-container [ngTemplateOutlet]="tpl"></ng-container>
            </div>
        </ng-template>`,
    animations: [
        slideContent('lc', 'translateX(-5000px)'),
        slideContent('rc', 'translateX(5000px)'),
        slideContent('tc', 'translateY(-5000px)'),
        slideContent('bc', 'translateY(5000px)')
    ]
})
export class AsideItemWithOverlayComponent implements OnInit {

    @Output()
    asideItemWithOverlayEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Input()
    icon: string = DEFAULT_ICON_CSS_CLASS;

    @Input()
    inheritedOverlayPosition: string;

    @ContentChild(TemplateRef) tpl;

    isOpen: boolean = false;

    stateExpression: 'void' | 'open' = 'open';
    overlayPosition = SPP_OVERLAY_POSITION_SIDEBAR_BOTTOM_RIGHT;

    constructor(private logger: LoggingService, private iAsideItemService: AsideItemService) {
    }

    ngOnInit(): void {
        this.logger.debug('setze Komponente mit ' + this.inheritedOverlayPosition);
        this.setOverlayPosition(this.inheritedOverlayPosition);
        this.iAsideItemService.$open.asObservable().subscribe((eventComponent: AsideItemWithOverlayComponent) => {
            if (this !== eventComponent) {
                this.isOpen = false;
            }
        });
    }

    /**
     * Status Übergänge.
     * @param ev
     */
    animationStarted(ev: any): void {
        this.logger.debug('Statuswechsel: ' + ev.fromState + ' ---> ' + ev.toState);
    }

    /**
     * setzt diverse Werte, die die Ausrichtung der Sidebar-Items betreffen.
     * @param ovelayposition
     */
    setOverlayPosition(ovelayposition: string): void {
        switch (ovelayposition) {
            case 'top-center':
                this.overlayPosition = SPP_OVERLAY_POSITION_SIDEBAR_TOP_CENTER;
                break;
            case 'right-center':
                this.overlayPosition = SPP_OVERLAY_POSITION_SIDEBAR_RIGHT_CENTER;
                break;
            case 'bottom-center':
                this.overlayPosition = SPP_OVERLAY_POSITION_SIDEBAR_BOTTOM_CENTER;
                break;
            case 'left-center':
                this.overlayPosition = SPP_OVERLAY_POSITION_SIDEBAR_LEFT_CENTER;
                break;
            default: // bottom-right ist default
                this.overlayPosition = SPP_OVERLAY_POSITION_SIDEBAR_BOTTOM_RIGHT;
                break;
        }
    }

    /**
     * öffnet und schließt die entsprechende Sidebar-Item-Komponente.
     */
    toggleSidebarItem() {
        this.isOpen = !this.isOpen;
        // An Komponente nach draußen propagieren (Output-Event)
        this.asideItemWithOverlayEmitter.emit(this.isOpen);
        // intern propagieren
        if (this.isOpen) {
            this.iAsideItemService.$open.next(this);
        }
    }
}
